import axios from "axios";
import { jwtDecode } from "jwt-decode";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8080/api/v1";

interface UserContext {
  context_id: string;
  role: string;
}

interface DecodedToken {
  sub: string;
  username?: string;
  email?: string;
  contexts?: UserContext[];
  exp: number;
  iat: number;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
}

interface RefreshResponse {
  message: string;
  access_token: string;
}

export const setTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem("jwt_token", accessToken);
  localStorage.setItem("refresh_token", refreshToken);
};

export const clearTokens = () => {
  localStorage.removeItem("jwt_token");
  localStorage.removeItem("refresh_token");
};

export const getAccessToken = (): string | null =>
  localStorage.getItem("jwt_token");

export const getRefreshToken = (): string | null =>
  localStorage.getItem("refresh_token");

export const isTokenExpired = (token: string): boolean => {
  try {
    const decoded: DecodedToken = jwtDecode(token);
    return Date.now() >= decoded.exp * 1000;
  } catch {
    return true;
  }
};

export const getDecodedToken = (): DecodedToken | null => {
  const token = getAccessToken();
  if (!token) return null;

  try {
    return jwtDecode<DecodedToken>(token);
  } catch {
    return null;
  }
};

export const getCurrentUserId = (): string | null => {
  const decoded = getDecodedToken();
  return decoded?.sub || null;
};

export const getCurrentUserContexts = (): UserContext[] => {
  const decoded = getDecodedToken();
  return decoded?.contexts || [];
};

export const hasPermission = (contextId: string, role: string): boolean => {
  const contexts = getCurrentUserContexts();

  const result = contexts.some(
    (context) => context.context_id === contextId && context.role === role,
  );

  return result;
};

export const isAdmin = (): boolean => {
  return hasPermission("administration", "admin");
};

export const loginUser = async (
  email: string,
  password: string,
): Promise<LoginResponse> => {
  try {
    const response = await axios.post<LoginResponse>(
      `${API_BASE_URL}/auth/login`,
      {
        email,
        password,
      },
    );
    const { access_token, refresh_token } = response.data;
    setTokens(access_token, refresh_token);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.error || "Login failed. Please try again.",
    );
  }
};

export const refreshToken = async (): Promise<string | null> => {
  const storedRefreshToken = getRefreshToken();
  if (!storedRefreshToken) {
    return null;
  }

  try {
    const response = await axios.post<RefreshResponse>(
      `${API_BASE_URL}/auth/refresh`,
      {
        token: storedRefreshToken,
      },
    );
    const { access_token } = response.data;
    localStorage.setItem("jwt_token", access_token);
    return access_token;
  } catch (error) {
    console.error("Failed to refresh token:", error);
    clearTokens();
    return null;
  }
};

export const logoutUser = async (): Promise<void> => {
  try {
    const token = getAccessToken();
    if (token) {
      await axios.post(
        `${API_BASE_URL}/auth/logout`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    }
  } catch (error) {
    console.error("Failed to log out:", error);
  } finally {
    clearTokens();
  }
};
